/**
 * GFG-405 — the Gift Links modal of a manual distribution campaign (prototype `cxLinks*`). Gifts
 * for Good sends nothing for these campaigns: the client hands the links out, so there are no
 * names, emails or resends here, only the links — find one, copy one, copy them all, export them,
 * or add more.
 * It reads the contacts envelope (D3): every row is one placeholder contact and its link, numbered
 * by its place in the campaign's full list (D5). COPY ALL LINKS copies every link of the campaign;
 * EXPORT CSV exports the rows the filters ask for at click time (D6), both via
 * `giftLinksExportSources`. ADD GIFT LINKS opens the wizard, or stays disabled with the reason
 * (`giftLinksAddGate`). MUI dialog, tooltip and snackbar; Tailwind on `ContactsDialog`.
 */
import AddLinkOutlinedIcon from "@mui/icons-material/AddLinkOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import { Alert, Button, Dialog, Snackbar, Tooltip } from "@mui/material";
import { type ReactNode, useCallback, useId, useMemo, useState } from "react";
import GfgButton from "@/components/ui/GfgButton";
import { GIFT_LINKS_COPY } from "@/constants/giftLinks";
import { useCampaignContacts } from "@/hooks/useCampaignContacts";
import { useRecipientCap } from "@/hooks/useRecipientCap";
import type { CampaignContactsResponse } from "@/interfaces/campaignContacts";
import { downloadCsv } from "@/lib/csv";
import type { CampaignStatus } from "@/types/types";
import { apiErrorMessage } from "@/utils/apiError-helper";
import { campaignCsvFilename } from "@/utils/campaignCsvFilename-helper";
import { capDialogOnAdd } from "@/utils/recipient-cap-helper";
import { resolveScheduleTimeZone } from "@/utils/timezone-helper";
import BudgetHistoryModal from "../contacts/BudgetHistoryModal";
import BudgetStrip from "../contacts/BudgetStrip";
import {
  CONTACTS_TABLE_PAPER_SX,
  CONTACTS_TEXT_LINK_SX,
  ContactsDialogHeader,
} from "../contacts/ContactsDialog";
import RecipientLimitDialog from "../contacts/RecipientLimitDialog";
import type { BudgetStripCopy } from "../contacts/types";
import GiftLinksAddWizard from "./GiftLinksAddWizard";
import GiftLinksTable, { GiftLinksToolbar, useGiftLinkFilters } from "./GiftLinksTable";
import {
  buildGiftLinksCsv,
  filterGiftLinks,
  giftLinksAddGate,
  giftLinksBanner,
  giftLinksCsvFilename,
  giftLinksExportGate,
  giftLinksExportSources,
  giftLinksSubtitle,
  toGiftLinkRows,
} from "./giftLinks-helper";
import type { GiftLinksAddGate, GiftLinkViewRow } from "./types";

export interface GiftLinksModalProps {
  open: boolean;
  /** Fired by the close control, the footer's Close, Escape and a backdrop click alike. */
  onClose: () => void;
  campaignId: string;
  campaignName: string;
  /** The card's status; the envelope's wins once loaded. */
  campaignStatus: CampaignStatus;
  /**
   * The card's pause (its status is "paused"): it blocks Add gift links, nothing else. The
   * envelope's own "paused" status blocks it too once loaded.
   */
  paused: boolean;
  /** May the caller edit THIS campaign? False opens the links read-only. */
  canEdit: boolean;
  /** GFG staff: links only fill freed spaces, never expand the budget. */
  replacementOnly?: boolean;
  /**
   * The campaign's per-gift price (the collection tier it was built on). When given, the Export CSV
   * download is named `Campaign Name - $Price.csv`; omitted (admin screens with no price to hand),
   * it falls back to the `campaign-<id>-gift-links.csv` scheme.
   */
  pricePerGift?: number;
}

/** What the snackbar is currently saying, and how loudly. */
interface ToastOutcome {
  message: string;
  severity: "success" | "error";
}

type ShowToast = (message: string, severity: ToastOutcome["severity"]) => void;

const T = GIFT_LINKS_COPY;

/** How long a confirmation or refusal stays up. */
const TOAST_MS = 4000;

/** The budget strip says "links" where the contacts modal says "gifts". */
const BUDGET_STRIP_COPY: BudgetStripCopy = {
  budgeted: T.budgetStrip.budgeted,
  free: T.budgetStrip.free,
  perGift: T.budgetStrip.perLink,
  separator: T.budgetStrip.separator,
  history: T.budgetStrip.history,
};

const ICON_SX = { fontSize: 15 } as const;

// ---- hooks ---------------------------------------------------------------------------------------

function useToast() {
  const [outcome, setOutcome] = useState<ToastOutcome | null>(null);
  // Held apart from `outcome` so the snackbar keeps its text through the exit transition.
  const [open, setOpen] = useState(false);
  // Stable, so the row copy built on it is too and the memoized rows stay put.
  const show = useCallback<ShowToast>((message, severity) => {
    setOutcome({ message, severity });
    setOpen(true);
  }, []);
  return { outcome, open, show, close: () => setOpen(false) };
}

/** Add gift links: null until the envelope is in, then the first reason it is blocked, if any. */
function useAddGate(
  data: CampaignContactsResponse | undefined,
  status: CampaignStatus,
  paused: boolean,
  canEdit: boolean,
  replacementOnly: boolean,
): GiftLinksAddGate | null {
  if (data === undefined) return null;
  return giftLinksAddGate({
    canEdit,
    manual: data.campaign.manual,
    status,
    multiUse: data.campaign.multiUse,
    paused,
    canAddGiftLinks: data.canAddGiftLinks,
    noFreeSpace: replacementOnly && data.spacesFree <= 0,
  });
}

/**
 * Copy, and say so either way. `navigator.clipboard` is undefined on an insecure origin and
 * `writeText` rejects when a permissions policy blocks it; both end in a message to act on.
 */
async function copyToClipboard(text: string, success: string, show: ShowToast): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    show(success, "success");
  } catch {
    show(T.toast.copyFailure, "error");
  }
}

/** One link per line: how many links a Copy all text holds. */
const lineCount = (text: string): number => (text === "" ? 0 : text.split("\n").length);

/** Copy one link (stable, for the memoized rows), or Copy all's text. */
function useCopyActions(copyAllText: string, show: ShowToast, canEdit: boolean) {
  const copyOne = useCallback(
    (row: GiftLinkViewRow) => {
      if (row.url !== null) void copyToClipboard(row.url, T.toast.copied, show);
    },
    [show],
  );
  const copyAll = () =>
    void copyToClipboard(copyAllText, T.toast.copiedAll(lineCount(copyAllText)), show);
  const gate = giftLinksExportGate(canEdit, lineCount(copyAllText), T.toast.nothingToCopy);
  return { copyOne, copyAll, copyAllDisabled: gate.disabled, copyAllReason: gate.reason };
}

/** The fetch, the rows and their filters, the gate, the dialogs it opens and the footer actions. */
function useGiftLinksModal(p: GiftLinksModalProps) {
  const query = useCampaignContacts(p.campaignId, p.open);
  const { data } = query;
  // The server's status wins once loaded: the card can lag behind it.
  const status = data?.campaign.status ?? p.campaignStatus;
  const rows = useMemo(() => toGiftLinkRows(data?.contacts ?? [], status), [data, status]);
  const filters = useGiftLinkFilters(rows, p.open);
  const gate = useAddGate(data, status, p.paused, p.canEdit, p.replacementOnly ?? false);
  const toast = useToast();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [limitOpen, setLimitOpen] = useState(false);
  const recipientCap = useRecipientCap();
  // GFG-439: at the plan's cap, Add gift links opens the limit dialog instead of the wizard. Each
  // link is one enabled contact, so the contacts list's length is the count the cap applies to.
  // GFG staff (`replacementOnly`) only refill freed spaces; the server still enforces the cap.
  const atCap =
    data !== undefined &&
    capDialogOnAdd({
      cap: recipientCap.cap,
      current: data.contacts.length,
      replacementOnly: p.replacementOnly ?? false,
      draft: false,
    });
  const openAdd = () => (atCap ? setLimitOpen(true) : setWizardOpen(true));
  const timeZone = resolveScheduleTimeZone(data?.campaign.scheduledTz);
  const { filtered } = filters; // Copy all's text (D6), memoized: 10,000 links.
  const sources = useMemo(() => giftLinksExportSources(rows, filtered), [rows, filtered]);
  const copy = useCopyActions(sources.copyAllText, toast.show, p.canEdit);
  // The identifiable `Campaign Name - $Price.csv` when the card hands over the tier price; the
  // id-based scheme otherwise (admin screens). A status filter appends the status to both so a
  // partial export (e.g. just Redeemed) isn't mistaken for the full list.
  const csvFilename =
    p.pricePerGift != null
      ? campaignCsvFilename(p.campaignName, p.pricePerGift, filters.activeStatus)
      : giftLinksCsvFilename(p.campaignId, filters.activeStatus);
  // The CSV is what was asked for at click time, not the deferred rows.
  const exportCsv = () => {
    const asked = filterGiftLinks(rows, filters.search, filters.activeStatus);
    const csv = buildGiftLinksCsv(giftLinksExportSources(rows, asked).csvRows, timeZone);
    downloadCsv(csvFilename, csv);
  };
  const dialogs = {
    wizardOpen,
    setWizardOpen,
    historyOpen,
    setHistoryOpen,
    limitOpen,
    setLimitOpen,
    recipientCap,
    openAdd,
  };
  const exportGate = giftLinksExportGate(p.canEdit, sources.csvRows.length);
  const exports = {
    ...copy,
    exportCsv,
    exportDisabled: exportGate.disabled,
    exportReason: exportGate.reason,
  };
  return { query, rows, filters, gate, toast, timeZone, ...dialogs, ...exports };
}

type GiftLinksModalState = ReturnType<typeof useGiftLinksModal>;

// ---- presentational pieces -----------------------------------------------------------------------

/** Prototype `.cmp-contacts-lock.is-closed` with the link icon. */
function ActivationBanner({ text }: { text: string }) {
  return (
    <div className="mt-3.5 flex items-center gap-2 rounded-lg border border-smoke bg-wax px-3.5 py-2.5 text-[13px] leading-[1.4] text-graphite">
      <LinkOutlinedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
      <span>{text}</span>
    </div>
  );
}

/** A footer button; disabled, it says why in a tooltip or in the text `describedBy` names. */
function FooterButton({
  label,
  icon,
  variant = "secondary",
  disabledReason = null,
  describedBy,
  disabled,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  variant?: "primary" | "secondary";
  disabledReason?: string | null;
  describedBy?: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip title={disabled ? (disabledReason ?? "") : ""} describeChild>
      <span className="inline-flex">
        <GfgButton
          type="button"
          variant={variant}
          disabled={disabled}
          aria-describedby={describedBy}
          onClick={onClick}
          startIcon={icon}
        >
          {label}
        </GfgButton>
      </span>
    </Tooltip>
  );
}

/** Close · Add gift links · Copy all links · Export CSV, and why Add is blocked while it is. */
function Footer({ m, onClose }: { m: GiftLinksModalState; onClose: () => void }) {
  const { gate } = m;
  const reasonId = useId();
  // Shown, not a tooltip: a disabled button takes no hover or focus (keyboard, screen readers).
  const addReason = gate !== null && !gate.allowed ? gate.reason : null;
  return (
    <>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <Button onClick={onClose} disableRipple sx={CONTACTS_TEXT_LINK_SX}>
          {T.footer.close}
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <FooterButton
            label={T.footer.add}
            icon={<AddLinkOutlinedIcon sx={ICON_SX} />}
            disabled={gate === null || !gate.allowed}
            describedBy={addReason === null ? undefined : reasonId}
            onClick={m.openAdd}
          />
          <FooterButton
            label={T.footer.copyAll}
            icon={<ContentCopyOutlinedIcon sx={ICON_SX} />}
            disabled={m.copyAllDisabled}
            disabledReason={m.copyAllReason}
            onClick={m.copyAll}
          />
          <FooterButton
            label={T.footer.exportCsv}
            icon={<FileDownloadOutlinedIcon sx={ICON_SX} />}
            variant="primary"
            disabled={m.exportDisabled}
            disabledReason={m.exportReason}
            onClick={m.exportCsv}
          />
        </div>
      </div>
      {addReason !== null && (
        <p id={reasonId} className="mt-2 text-right text-xs text-graphite">
          {addReason}
        </p>
      )}
    </>
  );
}

function Toast({ toast }: { toast: GiftLinksModalState["toast"] }) {
  return (
    <Snackbar
      open={toast.open}
      autoHideDuration={TOAST_MS}
      onClose={toast.close}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        severity={toast.outcome?.severity ?? "success"}
        variant="filled"
        onClose={toast.close}
        closeText={T.toast.dismiss}
      >
        {toast.outcome?.message}
      </Alert>
    </Snackbar>
  );
}

/** The budget strip with its gift-link wording; nothing until the campaign has a budget. */
function GiftLinksBudgetStrip({ m }: { m: GiftLinksModalState }) {
  const data = m.query.data;
  if (!data?.budget) return null;
  return (
    <BudgetStrip
      giftsBudgeted={data.budget.giftsBudgeted}
      spacesFree={data.spacesFree}
      pricePerGift={data.budget.pricePerGift}
      amendmentCount={data.budget.amendmentCount}
      onOpenHistory={() => m.setHistoryOpen(true)}
      copy={BUDGET_STRIP_COPY}
    />
  );
}

/** The dialogs opened over the modal: the add wizard and the budget history. */
function SideDialogs({
  m,
  campaignId,
  campaignName,
}: {
  m: GiftLinksModalState;
  campaignId: string;
  campaignName: string;
}) {
  const data = m.query.data;
  return (
    <>
      {/* The wizard closes itself after `onAdded`; the refetched envelope shows the new links. */}
      {data && (
        <GiftLinksAddWizard
          open={m.wizardOpen}
          onClose={() => m.setWizardOpen(false)}
          campaignId={campaignId}
          envelope={data}
          onAdded={(_result, message) => m.toast.show(message, "success")}
        />
      )}
      {m.recipientCap.cap !== null && m.recipientCap.planKey !== null && (
        <RecipientLimitDialog
          open={m.limitOpen}
          cap={m.recipientCap.cap}
          planKey={m.recipientCap.planKey}
          onClose={() => m.setLimitOpen(false)}
        />
      )}
      <BudgetHistoryModal
        open={m.historyOpen}
        onClose={() => m.setHistoryOpen(false)}
        campaignId={campaignId}
        campaignName={campaignName}
        timeZone={m.timeZone}
      />
    </>
  );
}

/** The subtitle, and the activation banner once the envelope is in (null before). */
function headerTexts(m: GiftLinksModalState, campaignName: string, now: Date) {
  const data = m.query.data;
  if (data === undefined) return { subtitle: campaignName, banner: null };
  return {
    subtitle: giftLinksSubtitle(campaignName, m.filters.filtered.length, m.rows.length),
    banner: giftLinksBanner({
      scheduledDate: data.campaign.scheduledDate,
      launchedAt: data.campaign.launchedAt,
      now,
      timeZone: m.timeZone,
    }),
  };
}

// ---- the modal -----------------------------------------------------------------------------------

export default function GiftLinksModal(props: GiftLinksModalProps) {
  const { open, onClose, campaignName } = props;
  const titleId = useId();
  const m = useGiftLinksModal(props);
  const { data, isLoading, isError, error } = m.query;
  const { subtitle, banner } = headerTexts(m, campaignName, new Date());

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        aria-labelledby={titleId}
        slotProps={{ paper: { sx: CONTACTS_TABLE_PAPER_SX } }}
      >
        <ContactsDialogHeader
          titleId={titleId}
          title={T.title}
          subtitle={subtitle}
          onClose={onClose}
        />
        {banner !== null && <ActivationBanner text={banner} />}
        <GiftLinksToolbar
          filters={m.filters}
          end={data?.budget ? <GiftLinksBudgetStrip m={m} /> : null}
        />
        <GiftLinksTable
          rows={m.filters.filtered}
          loading={isLoading}
          error={isError ? apiErrorMessage(error, T.errorFallback) : null}
          timeZone={m.timeZone}
          onCopy={props.canEdit ? m.copyOne : undefined}
        />
        <Footer m={m} onClose={onClose} />
        <Toast toast={m.toast} />
      </Dialog>
      <SideDialogs m={m} campaignId={props.campaignId} campaignName={campaignName} />
    </>
  );
}
